<script>
export default {
	name: 'AqTabs',
	props: {
		value: {
			type: String,
			default: null,
		},
		items: {
			type: Array,
			required: true,
		},
		label: {
			type: String,
			default: '',
		},
		size: {
			type: String,
			default: 'md',
		},
	},
	watch: {
		value() {
			this.$nextTick(this.revealActive)
		},
	},
	mounted() {
		this.revealActive()
	},
	methods: {
		revealActive() {
			const index = this.items.findIndex((item) => item.value === this.value)
			const tab = (this.$refs.tab || [])[index]
			if (!tab) return
			const list = this.$el
			list.scrollLeft = tab.offsetLeft - list.offsetLeft - (list.clientWidth - tab.offsetWidth) / 2
		},
		select(value) {
			if (value !== this.value) this.$emit('input', value)
		},
		move(index, step) {
			const next = (index + step + this.items.length) % this.items.length
			this.select(this.items[next].value)
			this.$nextTick(() => {
				const tabs = this.$refs.tab || []
				if (tabs[next]) tabs[next].focus()
			})
		},
	},
}
</script>

<template>
	<div class="aq-tabs" :class="`aq-tabs--${size}`" role="tablist" :aria-label="label || null">
		<button
			v-for="(item, index) in items"
			:key="item.value"
			ref="tab"
			type="button"
			role="tab"
			class="aq-tabs__item"
			:class="{ 'is-active': item.value === value }"
			:aria-selected="String(item.value === value)"
			:tabindex="item.value === value ? 0 : -1"
			@click="select(item.value)"
			@keydown.right.prevent="move(index, 1)"
			@keydown.left.prevent="move(index, -1)"
		>
			{{ item.label }}
		</button>
	</div>
</template>

<style scoped>
.aq-tabs {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}
.aq-tabs__item {
	display: inline-flex;
	align-items: center;
	min-height: 44px;
	padding: 8px 18px;
	border: 1px solid var(--aq-border-strong);
	border-radius: 999px;
	background: var(--aq-surface);
	color: var(--aq-ink-2);
	font-size: 15px;
	font-weight: 500;
	white-space: nowrap;
	cursor: pointer;
	transition: background 0.2s var(--aq-ease), border-color 0.2s var(--aq-ease),
		color 0.2s var(--aq-ease);
}
.aq-tabs__item:hover {
	border-color: var(--aq-primary);
	color: var(--aq-primary);
}
.aq-tabs__item:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(21, 127, 238, 0.25);
}
.aq-tabs__item.is-active {
	border-color: var(--aq-ink);
	background: var(--aq-ink);
	color: #fff;
}
.aq-tabs--sm {
	gap: 4px;
	padding: 4px;
	border-radius: 12px;
	background: var(--aq-bg);
	width: fit-content;
	max-width: 100%;
}
.aq-tabs--sm .aq-tabs__item {
	min-height: 38px;
	padding: 6px 14px;
	border-color: transparent;
	border-radius: 9px;
	background: transparent;
	font-size: 14px;
}
.aq-tabs--sm .aq-tabs__item:hover {
	border-color: transparent;
	background: var(--aq-surface);
}
.aq-tabs--sm .aq-tabs__item.is-active {
	border-color: var(--aq-border);
	background: var(--aq-surface);
	color: var(--aq-primary);
	box-shadow: 0 1px 3px rgba(21, 29, 72, 0.08);
}
@media (max-width: 768px) {
	.aq-tabs {
		flex-wrap: nowrap;
		overflow-x: auto;
		scrollbar-width: none;
		-webkit-overflow-scrolling: touch;
	}
	.aq-tabs::-webkit-scrollbar {
		display: none;
	}
}
</style>
